<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import KanbanAPI from '../../../../api/kanban';
import WootMessageEditor from '../../../../components/widgets/WootWriter/Editor.vue';
import WootInput from '../../../../../v3/components/Form/Input.vue';
import WootToggle from './Toggle.vue';
import WootSelect from '../../../../../v3/components/Form/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FunnelAPI from '../../../../api/funnel';
import { ALLOWED_FILE_TYPES, MAXIMUM_FILE_UPLOAD_SIZE } from 'shared/constants/messages';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({}),
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  funnels: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['saved', 'close']);
const { t } = useI18n();
const store = useStore();
const isLoading = ref(false);
const error = ref(null);
const errors = ref({});
const kanbanItems = ref([]);
const variablesPanelOpen = ref(false);
const attachment = ref(props.initialData.attachments?.[0] || null);
const attachmentInput = ref(null);

// Estado para controlar quando os dados estão prontos
const isDataReady = ref(false);

// Buscar todos os funis ao montar o componente
onMounted(async () => {
  try {
    isLoading.value = true;
    // Dispara a action para buscar todos os funis
    await store.dispatch('funnel/fetch');
  } catch (error) {
    console.error('Erro ao carregar funis:', error);
  } finally {
    isLoading.value = false;
  }
});

// Computed para obter todos os funis
const funnels = computed(() => props.funnels);

// Computed para obter o funil selecionado
const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

// Função para garantir que o valor é uma string
const ensureString = value => (value ? String(value) : '');

// Computed para obter as etapas do funil selecionado
const funnelStages = computed(() => {
  // Busca o funil diretamente dos records usando o ID atual
  const currentFunnel = funnels.value.find(
    f => String(f.id) === formData.value.funnel_id
  );

  let stages = [];

  if (currentFunnel?.stages) {
    stages = Object.entries(currentFunnel.stages)
      .map(([id, stage]) => ({
        value: String(id),
        label: stage.name,
        position: stage.position,
      }))
      .sort((a, b) => a.position - b.position);
  }

  // Se estamos editando e a etapa atual não está na lista, adiciona ela
  if (props.isEditing && props.initialData.stage_id) {
    const currentStageId = String(props.initialData.stage_id);
    const stageExists = stages.some(stage => stage.value === currentStageId);

    if (!stageExists) {
      // Adiciona a etapa atual como opção temporária
      stages.unshift({
        value: currentStageId,
        label: `Etapa ${currentStageId}`, // Label temporário
        position: 0,
      });
    }
  }

  return stages;
});

// Computed para transformar os funis em options para o select
const funnelOptions = computed(() => {
  const options = funnels.value.map(funnel => ({
    value: String(funnel.id),
    label: funnel.name,
  }));

  // Se estamos editando e o funil atual não está na lista, adiciona ele
  if (props.isEditing && props.initialData.funnel_id) {
    const currentFunnelId = String(props.initialData.funnel_id);
    const funnelExists = options.some(
      option => option.value === currentFunnelId
    );

    if (!funnelExists) {
      // Adiciona o funil atual como opção temporária
      options.unshift({
        value: currentFunnelId,
        label: `Funil ${currentFunnelId}`, // Label temporário
      });
    }
  }

  return options;
});

// Form data atualizado para incluir o funil_id
const formData = ref({
  funnel_id: ensureString(props.initialData.funnel_id),
  stage_id: ensureString(props.initialData.stage_id),
  title: props.initialData.title || '',
  content: props.initialData.content || '',
  attachments: props.initialData.attachments || [],
  is_default: props.initialData.is_default || false,
  webhook: {
    enabled: props.initialData.webhook?.enabled || false,
    url: props.initialData.webhook?.url || '',
    method: props.initialData.webhook?.method || 'POST',
  },
  conditions: {
    enabled: props.initialData.conditions?.enabled || false,
    rules: props.initialData.conditions?.rules || [],
  },
});

const attachmentFormats = 'imagens, áudios, vídeos, PDF, DOC/DOCX, XLS/XLSX, CSV, TXT, JSON, XML, ZIP e 7Z';

const selectAttachment = () => attachmentInput.value?.click();

const handleAttachment = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  if (!checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
    errors.value.attachment = t('KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.ATTACHMENT_SIZE', {
      size: MAXIMUM_FILE_UPLOAD_SIZE,
    });
    return;
  }

  errors.value.attachment = '';
  isLoading.value = true;
  try {
    const upload = new FormData();
    upload.append('attachment', file);
    const response = await window.axios.post(
      `/api/v1/accounts/${store.getters.getCurrentAccountId}/upload`,
      upload,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    attachment.value = {
      blob_id: response.data.blob_id,
      file_url: response.data.file_url,
      filename: file.name,
      content_type: file.type,
      size: file.size,
    };
    formData.value.attachments = [attachment.value];
  } catch {
    errors.value.attachment = t('KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.ATTACHMENT_UPLOAD');
  } finally {
    isLoading.value = false;
  }
};

const removeAttachment = () => {
  attachment.value = null;
  formData.value.attachments = [];
};

const kanbanTemplateVariables = computed(() => [
  {
    key: 'item_details.title',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.ITEM_DETAILS_TITLE'
    ),
  },
  {
    key: 'item_details.description',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.ITEM_DETAILS_DESCRIPTION'
    ),
  },
  {
    key: 'item_details.priority',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.ITEM_DETAILS_PRIORITY'
    ),
  },
  {
    key: 'item_details.agent_id',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.ITEM_DETAILS_AGENT_ID'
    ),
  },
  {
    key: 'item_details.conversation_id',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.ITEM_DETAILS_CONVERSATION_ID'
    ),
  },
  {
    key: 'item_details.value',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.ITEM_DETAILS_VALUE'
    ),
  },
  {
    key: 'funnel_stage',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.FUNNEL_STAGE'
    ),
  },
  {
    key: 'funnel.name',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.FUNNEL_NAME'
    ),
  },
  {
    key: 'funnel.description',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.FUNNEL_DESCRIPTION'
    ),
  },
  {
    key: 'funnel.id',
    description: t('KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.FUNNEL_ID'),
  },
]);

const contactConversationTemplateVariables = computed(() => [
  {
    key: 'conversation.id',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.CONVERSATION_ID'
    ),
  },
  {
    key: 'conversation.display_id',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.CONVERSATION_DISPLAY_ID'
    ),
  },
  {
    key: 'conversation.contact_name',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.CONVERSATION_CONTACT_NAME'
    ),
  },
  {
    key: 'contact.id',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.CONTACT_ID'
    ),
  },
  {
    key: 'contact.name',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.CONTACT_NAME'
    ),
  },
  {
    key: 'contact.first_name',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.CONTACT_FIRST_NAME'
    ),
  },
  {
    key: 'contact.last_name',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.CONTACT_LAST_NAME'
    ),
  },
  {
    key: 'contact.email',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.CONTACT_EMAIL'
    ),
  },
  {
    key: 'contact.phone_number',
    description: t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.CONTACT_PHONE'
    ),
  },
]);

const editorAdditionalTemplateVariables = computed(() => [
  ...kanbanTemplateVariables.value,
  ...contactConversationTemplateVariables.value.filter(v =>
    ['conversation.display_id', 'conversation.contact_name'].includes(v.key)
  ),
]);

const appendTemplateVariable = variableKey => {
  const token = `{{${variableKey}}}`;
  const current = formData.value.content?.trimEnd() || '';
  formData.value.content = current ? `${current}\n${token}` : token;
};

const variableToken = key => `{{${key}}}`;

// Validation
const validateForm = () => {
  errors.value = {};

  if (!selectedFunnel.value?.id) {
    errors.value.funnel = t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.FUNNEL_REQUIRED'
    );
  }

  if (!formData.value.stage_id) {
    errors.value.stage = t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.STAGE_REQUIRED'
    );
  }

  if (!formData.value.title.trim()) {
    errors.value.title = t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.TITLE_REQUIRED'
    );
  }

  if (!formData.value.content.trim()) {
    errors.value.content = t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.CONTENT_REQUIRED'
    );
  }

  if (formData.value.webhook.enabled && !formData.value.webhook.url.trim()) {
    errors.value.webhookUrl = t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.WEBHOOK_URL_REQUIRED'
    );
  }

  return Object.keys(errors.value).length === 0;
};

// Atualiza o handler de submit para incluir o funil e etapa
const handleSubmit = async () => {
  try {
    if (!validateForm()) return;

    isLoading.value = true;
    const currentFunnel = funnels.value.find(
      f => String(f.id) === formData.value.funnel_id
    );

    if (!currentFunnel) {
      throw new Error('Funil não encontrado');
    }

    // Cria uma cópia do funil mantendo a estrutura original
    const updatedFunnel = {
      ...currentFunnel,
      stages: { ...currentFunnel.stages }, // Clona stages para preservar IDs
    };

    const selectedStageId = formData.value.stage_id;
    const currentStage = updatedFunnel.stages[selectedStageId];

    // Preserva a estrutura existente da etapa
    updatedFunnel.stages[selectedStageId] = {
      ...currentStage,
      message_templates: [...(currentStage?.message_templates || [])],
    };

    if (props.isEditing) {
      const templateIndex = updatedFunnel.stages[
        selectedStageId
      ].message_templates.findIndex(t => t.id === props.initialData.id);

      if (templateIndex > -1) {
        updatedFunnel.stages[selectedStageId].message_templates[templateIndex] =
          {
            ...props.initialData,
            ...formData.value,
            updated_at: new Date().toISOString(),
          };
      }
    } else {
      updatedFunnel.stages[selectedStageId].message_templates.push({
        id: Date.now(),
        ...formData.value,
        created_at: new Date().toISOString(),
      });
    }

    await FunnelAPI.update(currentFunnel.id, updatedFunnel);
    await store.dispatch('funnel/fetch');
    emit('saved', formData.value);
  } catch (error) {
    errors.value = {
      submit: t('KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.SAVE_ERROR'),
    };
  } finally {
    isLoading.value = false;
  }
};

// Busca os itens do Kanban para extrair os campos disponíveis
const fetchKanbanItems = async () => {
  try {
    isLoading.value = true;
    const currentFunnel = store.getters['funnel/getSelectedFunnel'];

    if (!currentFunnel?.id) {
      console.error('Nenhum funil selecionado');
      return;
    }

    const response = await KanbanAPI.getItems(currentFunnel.id);

    // Verifica se response.data é um array antes de usar .map()
    if (Array.isArray(response.data)) {
      kanbanItems.value = response.data.map(item => ({
        id: item.id,
        title: item.item_details.title || '',
        description: item.item_details.description || '',
        priority: item.item_details.priority || 'none',
        assignee: item.item_details.assignee || null,
        position: item.position,
        funnel_stage: item.funnel_stage,
        item_details: item.item_details,
        custom_attributes: item.custom_attributes || {},
        createdAt: new Date(item.created_at).toLocaleDateString(),
      }));
    } else {
      console.warn('Response.data não é um array:', response.data);
      kanbanItems.value = [];
    }
  } catch (error) {
    console.error('Erro ao carregar itens do Kanban:', error);
    kanbanItems.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Computed para extrair campos únicos dos itens
const availableFields = computed(() => {
  return [
    { value: 'item_details.title', label: 'Título' },
    { value: 'item_details.description', label: 'Descrição' },
    { value: 'item_details.priority', label: 'Prioridade' },
    { value: 'item_details.agent_id', label: 'Agente' },
    { value: 'item_details.conversation_id', label: 'Conversa' },
    { value: 'item_details.value', label: 'Valor' },
    { value: 'funnel_stage', label: 'Etapa do Funil' },
  ].sort((a, b) => a.label.localeCompare(b.label));
});

// Computed para o v-model do funnel_id
const selectedFunnelId = computed({
  get: () => {
    return formData.value.funnel_id;
  },
  set: async value => {
    formData.value.funnel_id = ensureString(value);
    await handleFunnelChange(value);
  },
});

// Computed para o v-model do stage_id
const selectedStageId = computed({
  get: () => formData.value.stage_id,
  set: value => {
    formData.value.stage_id = ensureString(value);
  },
});

// Available HTTP methods for webhook
const httpMethods = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
];

const conditionOperatorOptions = computed(() => [
  {
    value: 'equals',
    label: t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.OPERATORS.EQUALS'),
  },
  {
    value: 'contains',
    label: t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.OPERATORS.CONTAINS'),
  },
  {
    value: 'starts_with',
    label: t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.OPERATORS.STARTS_WITH'),
  },
  {
    value: 'ends_with',
    label: t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.OPERATORS.ENDS_WITH'),
  },
]);

// Computed para controlar a exibição do editor de regras condicionais
const showConditionsEditor = computed(() => formData.value.conditions.enabled);

const addConditionRule = () => {
  formData.value.conditions.rules.push({
    field: '',
    operator: 'equals',
    value: '',
  });
};

const removeConditionRule = index => {
  formData.value.conditions.rules.splice(index, 1);
};

// Handler para quando um funil é selecionado
const handleFunnelChange = async value => {
  try {
    // Atualiza o ID do funil no formData
    formData.value.funnel_id = ensureString(value);

    // Busca o funil completo dos records do store
    const selectedFunnel = funnels.value.find(
      f => String(f.id) === formData.value.funnel_id
    );

    if (selectedFunnel) {
      // Atualiza o funil selecionado no store
      await store.dispatch('funnel/setSelectedFunnel', selectedFunnel);

      await nextTick();

      // Verifica se a etapa atual existe no novo funil
      const stages = Object.keys(selectedFunnel.stages || {});
      const currentStageId = formData.value.stage_id;
      const stageExists = stages.some(id => String(id) === currentStageId);

      // Só limpa a etapa se não estiver editando ou se a etapa não existir no novo funil
      if (!props.isEditing || !stageExists) {
        formData.value.stage_id = stages[0] || '';
      }
    }
  } catch (error) {
    console.error('Erro ao mudar funil:', error);
  }
};

// Carrega os itens quando o componente é montado
onMounted(async () => {
  try {
    isLoading.value = true;

    // Aguarda o DOM atualizar
    await nextTick();

    // Se houver um funil_id inicial, seleciona ele
    if (props.initialData.funnel_id) {
      const funnel = funnels.value.find(
        f => String(f.id) === ensureString(props.initialData.funnel_id)
      );

      if (funnel) {
        // Atualiza o funil selecionado no store
        await store.dispatch('funnel/setSelectedFunnel', funnel);

        // Verifica se a stage_id existe nas stages do funil
        const stages = Object.keys(funnel.stages || {});
        const initialStageId = ensureString(props.initialData.stage_id);
        const stageExists = stages.some(id => String(id) === initialStageId);

        // Aguarda o DOM atualizar
        await nextTick();

        // Força a atualização dos selects
        if (!stageExists) {
          formData.value.stage_id = stages[0] || '';
        }
      }
    }

    await nextTick();
    isDataReady.value = true;
  } catch (error) {
    console.error('Erro ao inicializar formulário:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <form
    class="message-template-form flex flex-col gap-3 min-w-0 max-w-full"
    @submit.prevent="handleSubmit"
  >
    <div
      class="flex flex-col gap-2 pb-1 sm:flex-row sm:items-center sm:justify-between"
    >
      <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
        {{
          t(
            isEditing
              ? 'KANBAN.MESSAGE_TEMPLATES.EDIT.TITLE'
              : 'KANBAN.MESSAGE_TEMPLATES.NEW'
          )
        }}
      </h3>
    </div>
    <div
    class="form-grid grid min-h-0 min-w-0 grid-cols-1 gap-4 lg:h-[calc(100vh-14rem)] lg:grid-cols-[minmax(20rem,0.8fr)_minmax(36rem,1.2fr)] lg:items-start lg:gap-6 lg:overflow-visible"
    >
      <!-- Coluna esquerda: configurações -->
      <div class="left-column no-scrollbar order-2 flex min-h-0 min-w-0 flex-col gap-3 lg:order-1 lg:h-full lg:overflow-y-auto lg:pr-2">
        <!-- Seleção de Etapa do Funil -->
        <div class="form-section">
          <h3 class="section-title">
            {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.FUNNEL_INFO') }}
          </h3>
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-slate-700 dark:text-slate-300">{{
              t('KANBAN.MESSAGE_TEMPLATES.FORM.DEFAULT_MESSAGE')
            }}</span>
            <WootToggle v-model="formData.is_default" />
          </div>
          <div
            class="mb-3 flex gap-2 rounded-lg border p-3 text-xs leading-relaxed"
            style="background-color: #eff6ff; border-color: #bfdbfe; color: #1e40af"
          >
            <fluent-icon
              icon="info"
              size="16"
              class="mt-0.5 shrink-0"
              style="color: #2563eb"
            />
            <span>
              {{
                t(
                  'KANBAN.MESSAGE_TEMPLATES.FORM.DEFAULT_MESSAGE_INFO'
                )
              }}
            </span>
          </div>

          <!-- Seletores -->
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-3">
            <div>
              <WootSelect
                v-model="selectedFunnelId"
                name="funnel"
                :label="t('KANBAN.MESSAGE_TEMPLATES.FORM.FUNNEL.LABEL')"
                :placeholder="t('KANBAN.MESSAGE_TEMPLATES.FORM.FUNNEL.PLACEHOLDER')"
                :options="funnelOptions"
              />
            </div>

            <div>
              <WootSelect
                v-model="selectedStageId"
                name="stage"
                :label="t('KANBAN.MESSAGE_TEMPLATES.FORM.STAGE.LABEL')"
                :placeholder="t('KANBAN.MESSAGE_TEMPLATES.FORM.STAGE.PLACEHOLDER')"
                :options="funnelStages"
              />
            </div>
          </div>
        </div>

        <!-- Informações Básicas (apenas título) -->
        <div class="form-section">
          <h3 class="section-title">
            {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.BASIC_INFO') }}
          </h3>

          <WootInput
            v-model="formData.title"
            name="title"
            :label="t('KANBAN.MESSAGE_TEMPLATES.FORM.TITLE.LABEL')"
            :placeholder="t('KANBAN.MESSAGE_TEMPLATES.FORM.TITLE.PLACEHOLDER')"
            :error="errors.title"
            class="mb-4"
          />
        </div>

        <!-- Configuração de Webhook -->
        <div class="form-section">
          <div class="flex items-center justify-between mb-4">
            <h3 class="section-title mb-0">
              {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.WEBHOOK.TITLE') }}
            </h3>
            <WootToggle v-model="formData.webhook.enabled" />
          </div>

          <div v-if="formData.webhook.enabled" class="webhook-config">
            <WootInput
              v-model="formData.webhook.url"
              name="webhook_url"
              :label="t('KANBAN.MESSAGE_TEMPLATES.FORM.WEBHOOK.URL.LABEL')"
              :placeholder="
                t('KANBAN.MESSAGE_TEMPLATES.FORM.WEBHOOK.URL.PLACEHOLDER')
              "
              :error="errors.webhookUrl"
            />

            <WootSelect
              v-model="formData.webhook.method"
              name="webhook_method"
              :label="t('KANBAN.MESSAGE_TEMPLATES.FORM.WEBHOOK.METHOD.LABEL')"
              :options="httpMethods"
            />
          </div>
        </div>

        <!-- Condições -->
        <div class="form-section">
          <div class="flex items-center justify-between mb-4">
            <h3 class="section-title mb-0">
              {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.TITLE') }}
            </h3>
            <WootToggle v-model="formData.conditions.enabled" />
          </div>

          <div v-if="showConditionsEditor" class="conditions-config space-y-4">
            <div
              v-for="(rule, index) in formData.conditions.rules"
              :key="index"
              class="condition-rule flex items-start gap-2"
            >
              <div class="flex-1">
                <div class="space-y-2">
                  <label
                    class="flex h-5 items-center text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{
                      t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.FIELD.LABEL')
                    }}
                  </label>
                  <select
                    v-model="rule.field"
                    class="h-10 w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-woot-500 focus:ring-1 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-700"
                  >
                    <option value="" disabled>
                      {{
                        t(
                          'KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.FIELD.PLACEHOLDER'
                        )
                      }}
                    </option>
                    <option
                      v-for="field in availableFields"
                      :key="field.value"
                      :value="field.value"
                    >
                      {{ field.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="w-32">
                <div class="space-y-2">
                  <label
                    class="flex h-5 items-center text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{
                      t(
                        'KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.OPERATOR.LABEL'
                      )
                    }}
                  </label>
                  <select
                    v-model="rule.operator"
                    class="h-10 w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-woot-500 focus:ring-1 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-700"
                  >
                    <option
                      v-for="op in conditionOperatorOptions"
                      :key="op.value"
                      :value="op.value"
                    >
                      {{ op.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="flex-1">
                <div class="space-y-2">
                  <label
                    class="flex h-5 items-center text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{
                      t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.VALUE.LABEL')
                    }}
                  </label>
                  <input
                    v-model="rule.value"
                    type="text"
                    class="h-10 w-full !mb-0 rounded-lg border border-slate-300 px-3 py-2 focus:border-woot-500 focus:ring-1 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-700"
                    :placeholder="
                      t(
                        'KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.VALUE.PLACEHOLDER'
                      )
                    "
                  />
                </div>
              </div>
              <Button
                variant="ghost"
                color="ruby"
                size="xs"
                class="mt-5"
                @click="removeConditionRule(index)"
              >
                <svg
                  class="h-4 w-4 shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <path d="M10 11v6M14 11v6" />
                </svg>
              </Button>
            </div>

            <Button
              variant="ghost"
              color="slate"
              size="xs"
              class="w-full"
              @click="addConditionRule"
            >
              <svg
                class="mr-2 h-4 w-4 shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.ADD_RULE') }}
            </Button>
          </div>
        </div>
      </div>

      <!-- Coluna direita: conteúdo e variáveis -->
      <div
        class="right-column no-scrollbar order-1 flex min-w-0 flex-col overflow-y-auto lg:order-2 lg:max-h-[calc(100vh-14rem)]"
      >
        <div
        class="editor-section flex min-h-0 flex-1 flex-col overflow-visible rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-800 sm:p-4"
        >
          <div class="mb-2 flex items-start justify-between gap-2">
            <h3
              class="section-title mb-0 text-sm font-semibold text-slate-800 dark:text-slate-100"
            >
              {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.TITLE') }}
            </h3>
            <button
              type="button"
              class="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-woot-600 transition-colors hover:bg-woot-50 dark:border-slate-600 dark:bg-slate-700 dark:text-woot-400 dark:hover:bg-slate-600"
              :aria-expanded="variablesPanelOpen"
              @click="variablesPanelOpen = !variablesPanelOpen"
            >
              {{
                variablesPanelOpen
                  ? t(
                      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES_TOGGLE_HIDE'
                    )
                  : t(
                      'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES_TOGGLE_SHOW'
                    )
              }}
              <svg
                v-if="variablesPanelOpen"
                class="h-3.5 w-3.5 shrink-0 text-current"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
              <svg
                v-else
                class="h-3.5 w-3.5 shrink-0 text-current"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </div>
          <p
            class="mb-2 text-xs leading-relaxed text-slate-600 dark:text-slate-400"
          >
            {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES_HINT') }}
          </p>
          <WootMessageEditor
            v-model="formData.content"
            enable-variables
            :additional-template-variables="editorAdditionalTemplateVariables"
            :placeholder="
              t('KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.PLACEHOLDER')
            "
            :error="errors.content"
            class="content-editor min-h-0 flex-1"
          />
          <div
            v-show="variablesPanelOpen"
            class="mt-3 max-h-64 overflow-y-auto pt-1"
          >
            <p
              class="mb-2 text-xs font-medium text-slate-700 dark:text-slate-300"
            >
              {{
                t('KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES_QUICK_TITLE')
              }}
            </p>
            <div class="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              <button
                v-for="variable in kanbanTemplateVariables"
                :key="variable.key"
                type="button"
                class="flex min-w-0 flex-col rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-left transition-all hover:border-woot-400 hover:shadow-sm active:scale-[0.99] dark:border-slate-600 dark:bg-slate-700 dark:hover:border-woot-500"
                :title="variable.description"
                @click="appendTemplateVariable(variable.key)"
              >
                <span
                  class="line-clamp-2 text-[11px] font-medium text-slate-800 dark:text-slate-100"
                >
                  {{ variable.description }}
                </span>
                <span
                  class="mt-0.5 truncate font-mono text-[10px] text-slate-500 dark:text-slate-400"
                >
                  {{ variableToken(variable.key) }}
                </span>
              </button>
            </div>
            <p
              class="mb-2 mt-4 text-xs font-medium text-slate-700 dark:text-slate-300"
            >
              {{
                t(
                  'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES_CONTACT_CONVERSATION_TITLE'
                )
              }}
            </p>
            <div class="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              <button
                v-for="variable in contactConversationTemplateVariables"
                :key="variable.key"
                type="button"
                class="flex min-w-0 flex-col rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-left transition-all hover:border-woot-400 hover:shadow-sm active:scale-[0.99] dark:border-slate-600 dark:bg-slate-700 dark:hover:border-woot-500"
                :title="variable.description"
                @click="appendTemplateVariable(variable.key)"
              >
                <span
                  class="line-clamp-2 text-[11px] font-medium text-slate-800 dark:text-slate-100"
                >
                  {{ variable.description }}
                </span>
                <span
                  class="mt-0.5 truncate font-mono text-[10px] text-slate-500 dark:text-slate-400"
                >
                  {{ variableToken(variable.key) }}
                </span>
              </button>
            </div>
            <p
              class="mt-2 text-[11px] leading-snug text-slate-500 dark:text-slate-400"
            >
              {{
                t(
                  'KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.VARIABLES.CUSTOM_ATTRIBUTES_NOTE'
                )
              }}
            </p>
          </div>
        </div>
        <div class="mt-3 rounded-xl border border-n-weak bg-n-surface-1 p-3">
          <div class="flex items-center justify-between gap-3">
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12">
                {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.ATTACHMENT.TITLE') }}
              </p>
              <p class="mt-1 text-xs text-n-slate-11">
                {{
                  t('KANBAN.MESSAGE_TEMPLATES.FORM.ATTACHMENT.INFO', {
                    formats: attachmentFormats,
                    size: MAXIMUM_FILE_UPLOAD_SIZE,
                  })
                }}
              </p>
            </div>
            <Button
              variant="outline"
              color="slate"
              size="xs"
              class="min-h-10 min-w-28 whitespace-nowrap"
              type="button"
              @click="selectAttachment"
            >
              {{
                attachment
                  ? t('KANBAN.MESSAGE_TEMPLATES.FORM.ATTACHMENT.CHANGE')
                  : t('KANBAN.MESSAGE_TEMPLATES.FORM.ATTACHMENT.ADD')
              }}
            </Button>
            <input
              ref="attachmentInput"
              type="file"
              class="hidden"
              :accept="ALLOWED_FILE_TYPES"
              @change="handleAttachment"
            />
          </div>
          <p v-if="errors.attachment" class="mt-2 text-xs text-n-ruby-9">
            {{ errors.attachment }}
          </p>
          <div
            v-if="attachment"
            class="mt-3 flex items-center justify-between rounded-lg bg-n-surface-2 px-3 py-2 text-sm"
          >
            <span class="truncate text-n-slate-12">{{ attachment.filename }}</span>
            <button
              type="button"
              class="ml-3 text-xs text-n-ruby-9"
              @click="removeAttachment"
            >
              {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.ATTACHMENT.REMOVE') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Botões de Ação -->
    <div
      class="form-actions sticky bottom-0 z-10 flex flex-wrap justify-end gap-2 bg-n-surface-2 pb-1 pt-3"
    >
      <Button variant="ghost" color="slate" size="sm" @click="emit('close')">
        {{ t('KANBAN.ACTIONS.CANCEL') }}
      </Button>
      <Button variant="solid" color="blue" size="sm" type="submit">
        {{ t('KANBAN.ACTIONS.SAVE') }}
      </Button>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.message-template-form {
  @apply min-w-0;
}

.left-column {
  -webkit-mask-image: linear-gradient(
    to bottom,
    black 0%,
    black calc(100% - 2.5rem),
    transparent 100%
  );
  mask-image: linear-gradient(
    to bottom,
    black 0%,
    black calc(100% - 2.5rem),
    transparent 100%
  );
}

.form-section {
  @apply rounded-xl border border-n-weak bg-n-surface-1 px-4 py-3 dark:bg-n-surface-1 sm:px-5 sm:py-4;
}

.section-title {
  @apply mb-3 text-sm font-semibold text-n-slate-12;
}

.webhook-config,
.conditions-config {
  @apply mt-3;
}

.condition-rule {
  @apply rounded-lg border border-n-weak bg-n-surface-2 p-3;
}

.content-editor {
  @apply min-h-[24rem] flex-1;
  :deep(.woot-editor) {
    @apply min-h-[24rem] overflow-visible rounded-lg border border-slate-200 bg-white p-2 dark:border-slate-600 dark:bg-slate-700;
  }
  :deep(.ProseMirror-woot-style) {
    min-height: 24rem !important;
    max-height: none !important;
    overflow: visible !important;
  }
}
</style>
